import { SimpleOptionType } from '@terragotech/form-renderer';
import {
  AggregateConfig,
  CommandVersionDefinition,
  EventVersionDefinition,
  Gen5Config,
  PropertyCollection,
  WebUIConfig,
} from '@terragotech/gen5-config-lib';
import {
  AGGREGATE_DEFAULT_KEYS,
  MAP_SERVICE_DEFAULT_KEYS,
  MAP_SERVICE_TYPE,
  FLOOR_PLAN_DEFAULT_KEYS,
  BUILDING_DEFAULT_KEYS,
  NOT_MAP_ASSETS_NAMES,
} from '@terragotech/gen5-shared-utilities';
import { JSONSchema6 } from 'json-schema';
import _ from 'lodash';
import {
  commandActionButtonActionToRef,
  defaultAggregateUICustomization,
  generateAggregateByName,
  generateNewCommandAndVersions,
  generateNewEventAndVersions,
} from './jsonPartsGenerators';
import { getAggregateIndex } from './navigationUtils';
import {
  ActionButtonRow,
  AggregateDefault,
  CommandVersionDefault,
  EventVersionDefault,
  FabAction,
  WebUIDefault,
} from './types';
import { camelCase } from 'change-case';
import { FunctionDefinition } from '@terragotech/gen5-datamapping-lib';

//#region MapService
const MAP_SERVICE_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: MAP_SERVICE_DEFAULT_KEYS.typeName,
  singularName: 'Map Service',
  properties: {
    symbolKey: {
      uiType: 'Hidden',
    },
    serviceType: {
      nullable: false,
      label: 'Service Type',
      type: 'String',
    },
    serviceUri: {
      nullable: true,
      label: 'Service URL',
      type: 'String',
    },
    layers: {
      nullable: true,
      label: 'Layers',
      type: 'JSON',
      uiType: 'JSON',
    },
  },
  isReferenceType: true,
};
MAP_SERVICE_AGGREGATE_DEFAULT.events = {
  mapServiceCreatedEvent: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          properties: {
            data: {
              type: 'object',
              properties: {
                serviceType: { type: 'string' },
                serviceUri: { type: 'string' },
                serviceTitle: { type: 'string' },
                layerGroup: { type: 'string' },
              },
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: { type: 'EVENT', config: {}, inputs: { data: {} } },
            STATE: { type: 'STATE', config: {}, inputs: {} },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceTitle',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.userName',
                },
                id: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.aggregateId',
                },
                serviceType: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceType',
                },
                serviceUri: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.serviceUri',
                },
                layers: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.layerGroup',
                },
                symbolKey: 'symbol_nodeonly_000000',
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};
MAP_SERVICE_AGGREGATE_DEFAULT.commands = {
  createMapService: {
    defaultLabel: `Create ${
      MAP_SERVICE_AGGREGATE_DEFAULT.singularName ?? MAP_SERVICE_AGGREGATE_DEFAULT.typeName
    }`,
    versions: [
      {
        version: 1,
        commandType: 'FORM',
        isCreationCommand: true,
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          ...(true && { eventName: 'mapServiceCreatedEvent' }), // Not applicable for 'ONE-TO-MANY' mappingType; defaults to ''.
          mapping: {
            nodes: {
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: `${MAP_SERVICE_AGGREGATE_DEFAULT.typeName}::mapServiceCreatedEvent::1`,
                  aggregateType: MAP_SERVICE_AGGREGATE_DEFAULT.typeName,
                },
                inputs: {
                  timestamp: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.timestamp',
                  },
                  aggregateId: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  data: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.data',
                  },
                },
              },
              newNode1: { type: 'COMMAND_DATA', config: {} },
              newNode2: { type: 'ID-GENERATOR', config: {} },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        formDefinition: {
          order: ['serviceType', 'serviceUri', 'serviceTitle', 'layerGroup'],
          components: {
            serviceType: {
              type: 'select',
              label: 'Service Type',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: SimpleOptionType,
                items: [
                  { value: MAP_SERVICE_TYPE.WMS, label: 'OpenGIS Web Map Service (WMS)' },
                  { value: MAP_SERVICE_TYPE.ArcGIS, label: 'ArcGIS Map Server' },
                ],
              },
              droppableId: 'form',
            },
            serviceUri: {
              type: 'textInput',
              label: 'Map Service URL',
              placeholder: 'https://www.xyzmapservice.com/api/',
              required: true,
              info: 'A list of the available layers and data are provided by the service endpoint. For WMS, you can view this data via its "GetCapabilities" request. E.g. https://www.xyzmapservice.com/api?request=getcapabilities&service=WMS&version=1.3.0',
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            serviceTitle: {
              type: 'textInput',
              label: 'Map Service Title',
              placeholder: 'XYZ Map Service',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              droppableId: 'form',
            },
            layerGroup: {
              template: {
                order: ['layerName', 'layerTitle', 'layerLegendUri'],
                components: {
                  layerName: {
                    type: 'textInput',
                    label: 'Name',
                    placeholder: 'layer_name',
                    required: true,
                    info: 'Comma-separated list of layer Names (e.g. abc,xyz) or IDs (e.g. 10,17,25). For WMS, the layer Name of the Layer XML tag returned from the API. For ArcGIS, the unique ID of the layer. See URL field info for more details.',
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                  layerTitle: {
                    type: 'textInput',
                    label: 'Title',
                    placeholder: 'Layer Name',
                    required: false,
                    info: 'A custom title for the layer to display in the app. For WMS, this may be obtained from the Title tag on the Layer returned from the API. For ArcGIS, this will be the layer name alongside its unique ID. See URL field info for more details.',
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                  layerLegendUri: {
                    type: 'textInput',
                    label: 'Layer Legend URL',
                    placeholder: 'https://www.xyzmapservice.com/layerLegend.png',
                    required: false,
                    info: "A link to the image to show for the map layer's legend or key. For WMS, this is sometimes provided by the LegendURL tag returned from the API. For ArcGIS, the map server may provide a Legend endpoint instead. See URL field info for more details.",
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    droppableId: 'layerGroup',
                  },
                },
              },
              type: 'group',
              label: 'Layers',
              repeats: {
                enabled: true,
                min: 1,
                max: -1,
                addButtonText: 'Add Layer',
                deleteConfirmationMessage: 'Are you sure you want to delete this layer?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
            },
          },
        },
      },
    ],
  },
};
const MAP_SERVICE_WEBUI_DEFAULT: WebUIDefault = {
  fabActions: [
    {
      aggregateName: MAP_SERVICE_AGGREGATE_DEFAULT.typeName,
      button: commandActionButtonActionToRef({
        action: `createMapService ver:1`,
        label: `Add ${
          MAP_SERVICE_AGGREGATE_DEFAULT.singularName ?? MAP_SERVICE_AGGREGATE_DEFAULT.typeName
        }`,
        icon: 'fa-object-ungroup',
        conditionalMap: {
          nodes: {
            METADATA: { type: 'METADATA', config: {} },
            BUTTON_STATE: {
              type: 'BUTTON-STATE',
              config: {},
              inputs: {
                isHidden: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
            },
            newNode1: {
              type: 'FIXED-REGEX',
              config: {},
              inputs: {
                text: {
                  sourceObject: 'METADATA',
                  sourcePath: '$.userInfo.permissionString',
                },
                regex: `(:?^|,)(${MAP_SERVICE_DEFAULT_KEYS.permissionKey})(:?$|,)`,
              },
            },
            newNode2: {
              type: 'NOT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.match',
                },
              },
            },
          },
          outputDefinition: { outputNode: 'BUTTON_STATE' },
        },
      } as ActionButtonRow),
    },
  ],
  aggregateUICustomizations: {
    [MAP_SERVICE_AGGREGATE_DEFAULT.typeName]: {
      hiddenFromMapView: true,
    },
  },
};

export const generateMapServiceAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, 'WebMapServiceSource'); // Remove deprecated aggregate definition.
  return generateAggregateData(config, MAP_SERVICE_AGGREGATE_DEFAULT, MAP_SERVICE_WEBUI_DEFAULT);
};
export const removeMapServiceAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, 'WebMapServiceSource'); // Remove deprecated aggregate definition.
  return removeAggregateData(config, MAP_SERVICE_AGGREGATE_DEFAULT.typeName);
};
//#endregion

//#region FloorPlan
const FLOOR_PLAN_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: FLOOR_PLAN_DEFAULT_KEYS.typeName,
  singularName: 'Floor Plan',
  properties: {
    symbolKey: {
      uiType: 'Hidden',
    },
    [FLOOR_PLAN_DEFAULT_KEYS.parentKey]: {
      nullable: true,
      label: 'Base Floor Plan',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    },
    subFloorPlans: {
      nullable: true,
      label: 'Sub Floor Plans',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-MANY',
      foreignColumn: FLOOR_PLAN_DEFAULT_KEYS.parentKey,
    },
    building: {
      nullable: true,
      label: 'Building',
      type: BUILDING_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    },
    image: {
      nullable: false,
      label: 'Image',
      type: 'PhotoCollection',
    },
  },
  isReferenceType: false,
};
FLOOR_PLAN_AGGREGATE_DEFAULT.events = {
  floorPlanCreated: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['floorPlanCreated'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                image: {
                  type: 'array',
                  items: { type: 'string' },
                },
                building: { type: 'string' },
                baseFloorPlan: { type: 'string' },
                project: { type: 'string' },
              },
              required: ['title', 'baseFloorPlan', 'building', 'image', 'project'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: { x: 134.3125, y: 50 },
            },
            STATE: {
              type: 'STATE',
              config: {},
              inputs: {},
              position: { x: 138.265625, y: 1057.1875 },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.title',
                },
                image: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.image',
                },
                building: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.building',
                },
                parentFloorPlan: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.parentFloorPlan',
                },
                baseFloorPlan: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.baseFloorPlan',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.email',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
              },
              position: {
                x: 644.1164021164021,
                y: 425.09810405643736,
              },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};
FLOOR_PLAN_AGGREGATE_DEFAULT.commands = {
  createFloorPlan: {
    defaultLabel: 'Create Floor Plan',
    versions: [
      {
        version: 1,
        isCreationCommand: true,
        cmdToEventsMapping: {
          mappingType: 'ONE-TO-MANY',
          mapping: {
            nodes: {
              COMMAND: {
                type: 'DATA',
                config: {},
                position: {
                  x: -561.137290996003,
                  y: -44.61228618407615,
                },
              },
              OUTPUT: {
                type: 'OUTPUT-EVENT',
                config: {
                  objectSchema: 'Building::buildingCreated::1',
                  aggregateType: 'Building',
                },
                inputs: {
                  outputEvent: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  aggregateId: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  data: {
                    title: {
                      sourceObject: 'COMMAND',
                      sourcePath: '$.buildingName',
                    },
                    project: {
                      sourceObject: 'newNode9',
                      sourcePath: '$.output',
                    },
                    lat: {
                      sourceObject: 'newNode11',
                      sourcePath: '$.latitude',
                    },
                    lon: {
                      sourceObject: 'newNode11',
                      sourcePath: '$.longitude',
                    },
                  },
                },
                position: {
                  x: 807.9419635660166,
                  y: -137.98725415121748,
                },
              },
              newNode1: {
                type: 'STRING-EQUALS',
                config: {},
                inputs: {
                  firstSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.linkTo',
                  },
                  secondSource: 'newBuilding',
                },
                position: {
                  x: -140.3365288011924,
                  y: -105.87583239746812,
                },
              },
              newNode2: {
                type: 'ID-GENERATOR',
                config: {},
                position: {
                  x: 388.09564835490033,
                  y: -11.918103572022797,
                },
              },
              newNode3: {
                type: 'ARRAY-ITERATOR',
                config: {},
                inputs: {
                  input: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.createFloorPlan',
                  },
                },
                position: {
                  x: 224.62767941032666,
                  y: 159.98442490721052,
                },
              },
              newNode4: {
                type: 'OUTPUT-EVENT',
                config: {
                  name: 'Output Event',
                  aggregateType: 'FloorPlan',
                  objectSchema: 'FloorPlan::floorPlanCreated::1',
                },
                inputs: {
                  aggregateId: {
                    sourceObject: 'newNode3',
                    sourcePath: '$.id',
                  },
                  data: {
                    baseFloorPlan: {
                      sourceObject: 'newNode6',
                      sourcePath: '$.output',
                    },
                    building: {
                      sourceObject: 'newNode5',
                      sourcePath: '$.output',
                    },
                    title: {
                      sourceObject: 'newNode8',
                      sourcePath: '$.title',
                    },
                    image: {
                      sourceObject: 'newNode8',
                      sourcePath: '$.image',
                    },
                    project: {
                      sourceObject: 'newNode9',
                      sourcePath: '$.output',
                    },
                  },
                },
                name: 'Output Event',
                position: {
                  x: 917.4232449482005,
                  y: 198.3130329837099,
                },
              },
              newNode5: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'newNode2',
                    sourcePath: '$.newId',
                  },
                  falseSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.building',
                  },
                },
                position: {
                  x: 594.1236017973164,
                  y: 31.203508338179507,
                },
              },
              newNode6: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode7',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.baseFloorPlan',
                  },
                },
                position: {
                  x: 204.08652233870475,
                  y: 611.287955356442,
                },
              },
              newNode7: {
                type: 'STRING-EQUALS',
                config: {},
                inputs: {
                  firstSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.linkTo',
                  },
                  secondSource: 'existingFloorPlan',
                },
                position: {
                  x: -41.87230876169522,
                  y: 309.7618094484255,
                },
              },
              newNode8: {
                type: 'OBJECT-ATTRIBUTE-LOOKUP',
                config: {
                  props: [
                    ['String', 'title'],
                    ['Photo Array', 'image'],
                  ],
                },
                inputs: {
                  input: {
                    sourceObject: 'newNode3',
                    sourcePath: '$.output',
                  },
                },
                position: {
                  x: 227.22027290448344,
                  y: 272.30537280701753,
                },
              },
              newNode9: {
                type: 'CONDITIONAL-MAPPER',
                config: { type: 'String' },
                inputs: {
                  booleanSource: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.output',
                  },
                  trueSource: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.project',
                  },
                  falseSource: {
                    sourceObject: 'newNode10',
                    sourcePath: '$.project',
                  },
                },
                position: {
                  x: 37.318939345720935,
                  y: -94.40934659399522,
                },
              },
              newNode10: {
                type: 'SINGLE-AGGREGATE-LOOKUP',
                config: { aggregateType: 'Building' },
                inputs: {
                  aggregateId: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.building',
                  },
                },
                position: {
                  x: -250.61897023182632,
                  y: -106.67019704321052,
                },
              },
              newNode11: {
                type: 'GEOJSON-PARSER',
                config: {},
                inputs: {
                  source: {
                    sourceObject: 'COMMAND',
                    sourcePath: '$.location.location',
                  },
                },
                position: { x: 457, y: -192.2265625 },
              },
            },
            outputDefinition: {
              outputQuery: { types: ['OUTPUT-EVENT'] },
            },
          },
        },
        commandType: 'FORM',
        formDefinition: {
          order: [
            'linkTo',
            'project',
            'location',
            'buildingName',
            'building',
            'baseFloorPlan',
            'createFloorPlan',
          ],
          initialDataMap: {
            nodes: {
              STATE: {
                type: 'STATE',
                name: 'Current State',
                inputs: {},
              },
              OUTPUT: {
                type: 'OBJECT-BUILDER',
                name: 'Form State',
                inputs: {},
                config: { objectSchema: 'FORM' },
              },
            },
            outputDefinition: { outputNode: 'OUTPUT' },
          },
          components: {
            linkTo: {
              type: 'radio',
              label: 'Link To',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'SimpleOption',
                items: [
                  { value: 'newBuilding', label: 'New Building' },
                  {
                    value: 'existingBuilding',
                    label: 'Existing Building',
                  },
                  {
                    label: 'Existing Floor Plan',
                    value: 'existingFloorPlan',
                  },
                ],
              },
              droppableId: 'form',
            },
            project: {
              type: 'select',
              label: 'Project',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'AggregateLoader',
                aggregateType: 'Project',
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 466.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 799.1875, y: 409.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 457, y: 308 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            location: {
              type: 'location',
              label: 'Location',
              placeholder: 'Enter text',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 514.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 985.1875, y: 553.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 443, y: 417 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            buildingName: {
              type: 'textInput',
              label: 'Building Name',
              placeholder: 'XYZ Building',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode1',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 646.1875, y: 429.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'newBuilding',
                    },
                    position: { x: 331, y: 275 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              errorMap: [
                {
                  name: 'Duplicate Name',
                  description: '',
                  nodes: {
                    FORM: {
                      type: 'FORM',
                      config: {},
                      inputs: {},
                      position: { x: 113.40234375, y: 50 },
                    },
                    FORMVALUE: {
                      type: 'FORMVALUE',
                      config: {},
                      inputs: {},
                      position: { x: 119.58984375, y: 442.59375 },
                    },
                    FIELD_ERROR: {
                      type: 'FORM-ERROR',
                      config: {},
                      inputs: {
                        errorMessage: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                      },
                      position: { x: 790, y: 497.1875 },
                    },
                    newNode1: {
                      type: 'CONDITIONAL-MAPPER',
                      config: {
                        type: 'String',
                        name: 'Conditional Mapper',
                      },
                      inputs: {
                        booleanSource: {
                          sourceObject: 'newNode2',
                          sourcePath: '$.exists',
                        },
                        trueSource: 'Building name already exists',
                      },
                      name: 'Conditional Mapper',
                      position: { x: 521, y: 257 },
                    },
                    newNode2: {
                      type: 'AGGREGATE-SEARCH',
                      config: {
                        aggregateType: 'Building',
                        attributeName: 'title',
                      },
                      inputs: {
                        attributeValueSource: {
                          sourceObject: 'FORMVALUE',
                          sourcePath: '$.value',
                        },
                      },
                      position: { x: 178, y: 340 },
                    },
                  },
                  outputDefinition: { outputNode: 'FIELD_ERROR' },
                },
              ],
              droppableId: 'form',
            },
            building: {
              type: 'select',
              label: 'Building',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'AggregateLoader',
                aggregateType: 'Building',
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 418.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode2',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 815.1875, y: 356.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingBuilding',
                    },
                    position: { x: 348, y: 274 },
                  },
                  newNode2: {
                    type: 'OR',
                    config: {},
                    inputs: {
                      sources: [
                        {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                        {
                          sourceObject: 'newNode3',
                          sourcePath: '$.output',
                        },
                      ],
                    },
                    position: { x: 577, y: 488 },
                  },
                  newNode3: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingFloorPlan',
                    },
                    position: { x: 132, y: 541 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            baseFloorPlan: {
              type: 'select',
              label: 'Base Floor Plan',
              placeholder: 'placeholder',
              required: true,
              repeats: {
                enabled: false,
                min: 1,
                max: 1,
                addButtonText: 'Add Field',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
              options: {
                type: 'DynamicLoader',
                optionsMap: {
                  nodes: {
                    FORM: {
                      type: 'FORM',
                      config: {},
                      inputs: {},
                      position: { x: 100.3359375, y: 50 },
                    },
                    FORMVALUE: {
                      type: 'FORMVALUE',
                      config: {},
                      inputs: {},
                      position: { x: 114.5234375, y: 442.1875 },
                    },
                    DROPDOWN_OPTIONS: {
                      type: 'DROPDOWN-OPTIONS',
                      config: {},
                      inputs: {
                        options: {
                          sourceObject: 'newNode2',
                          sourcePath: '$.transformed',
                        },
                      },
                      position: { x: 884, y: 218.375 },
                    },
                    newNode1: {
                      type: 'AGGREGATE-SEARCH',
                      config: {
                        aggregateType: 'FloorPlan',
                        attributeName: 'building',
                      },
                      inputs: {
                        attributeValueSource: {
                          sourceObject: 'FORM',
                          sourcePath: '$.building',
                        },
                      },
                      position: {
                        x: 109.65801668211309,
                        y: 58.58016682113068,
                      },
                    },
                    newNode2: {
                      type: 'ARRAY-TRANSFORM',
                      config: {
                        type: 'Object',
                        functionId: 'transformFloorPlans::1',
                        inputName: 'in',
                        outputName: 'result',
                        inputData: false,
                      },
                      inputs: {
                        data: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.itemsArray',
                        },
                        input: {
                          sourceObject: 'newNode1',
                          sourcePath: '$.itemsArray',
                        },
                      },
                      position: {
                        x: 543.002780352178,
                        y: 322.41334569045415,
                      },
                    },
                  },
                  outputDefinition: { outputNode: 'DROPDOWN_OPTIONS' },
                },
              },
              conditionalMap: {
                nodes: {
                  FORM: {
                    type: 'FORM',
                    config: {},
                    inputs: {},
                    position: { x: 50, y: 50 },
                  },
                  FORMVALUE: {
                    type: 'FORMVALUE',
                    config: {},
                    inputs: {},
                    position: { x: 64.1875, y: 442.1875 },
                  },
                  FORM_CONDITIONAL: {
                    type: 'FORM-CONDITIONAL',
                    config: {},
                    inputs: {
                      isVisible: {
                        sourceObject: 'newNode2',
                        sourcePath: '$.output',
                      },
                    },
                    position: { x: 1080.1875, y: 372.375 },
                  },
                  newNode1: {
                    type: 'STRING-EQUALS',
                    config: {},
                    inputs: {
                      firstSource: {
                        sourceObject: 'FORM',
                        sourcePath: '$.linkTo',
                      },
                      secondSource: 'existingFloorPlan',
                    },
                    position: { x: 404, y: 373 },
                  },
                  newNode2: {
                    type: 'AND',
                    config: {},
                    inputs: {
                      sources: [
                        {
                          sourceObject: 'newNode1',
                          sourcePath: '$.output',
                        },
                        {
                          sourceObject: 'newNode3',
                          sourcePath: '$.exists',
                        },
                      ],
                    },
                    position: { x: 811, y: 140 },
                  },
                  newNode3: {
                    type: 'TEXT-EXISTS',
                    config: {},
                    inputs: {
                      source: {
                        sourceObject: 'FORM',
                        sourcePath: '$.building',
                      },
                    },
                    position: { x: 331, y: 123 },
                  },
                },
                outputDefinition: { outputNode: 'FORM_CONDITIONAL' },
              },
              droppableId: 'form',
            },
            createFloorPlan: {
              template: {
                order: ['title', 'image'],
                components: {
                  title: {
                    type: 'textInput',
                    label: 'Name',
                    placeholder: 'XYZ Floor Plan',
                    required: true,
                    repeats: {
                      enabled: false,
                      min: 1,
                      max: 1,
                      addButtonText: 'Add Field',
                      deleteConfirmationMessage: 'Are you sure you want to delete this?',
                      allowReordering: true,
                      allowInitialDataDelete: true,
                      allowAdding: true,
                    },
                    errorMap: [
                      {
                        name: 'Duplicate Names within Building',
                        description: '',
                        nodes: {
                          FORM: {
                            type: 'FORM',
                            config: {},
                            inputs: {},
                            position: {
                              x: -231.69454741379917,
                              y: 198.0639993330821,
                            },
                          },
                          FORMVALUE: {
                            type: 'FORMVALUE',
                            config: {},
                            inputs: {},
                            position: {
                              x: -538.4512673930594,
                              y: 638.2756615878955,
                            },
                          },
                          FIELD_ERROR: {
                            type: 'FORM-ERROR',
                            config: {},
                            inputs: {
                              errorMessage: {
                                sourceObject: 'newNode7',
                                sourcePath: '$.output',
                              },
                            },
                            position: {
                              x: 1996.7416954922771,
                              y: 289.82693713172364,
                            },
                          },
                          newNode1: {
                            type: 'ARRAY-FILTER',
                            config: {
                              type: 'Object',
                              functionId: 'filterDuplicateFloorPlanNames::1',
                              inputName: 'in',
                              outputName: 'result',
                              inputData: true,
                            },
                            inputs: {
                              input: {
                                sourceObject: 'newNode4',
                                sourcePath: '$.itemsArray',
                              },
                              data: {
                                floorPlanName: {
                                  sourceObject: 'FORMVALUE',
                                  sourcePath: '$.createFloorPlan.title',
                                },
                              },
                            },
                            position: {
                              x: 559.8788147111723,
                              y: -43.983047105018784,
                            },
                          },
                          newNode4: {
                            type: 'AGGREGATE-SEARCH',
                            config: {
                              aggregateType: 'FloorPlan',
                              attributeName: 'building',
                            },
                            inputs: {
                              attributeValueSource: {
                                sourceObject: 'FORM',
                                sourcePath: '$.building',
                              },
                            },
                            position: {
                              x: 126.14560439715184,
                              y: -4.5642637908086385,
                            },
                          },
                          newNode5: {
                            type: 'ARRAY-LENGTH',
                            config: {},
                            inputs: {
                              input: {
                                sourceObject: 'newNode1',
                                sourcePath: '$.filtered',
                              },
                            },
                            position: {
                              x: 1092.625848362925,
                              y: 262.31047506070695,
                            },
                          },
                          newNode6: {
                            type: 'NUMBER-COMPARE',
                            config: {},
                            inputs: {
                              baseNumber: {
                                sourceObject: 'newNode5',
                                sourcePath: '$.length',
                              },
                              compareNumber: 0,
                            },
                            position: {
                              x: 1293.7686645476251,
                              y: 401.99793165330857,
                            },
                          },
                          newNode7: {
                            type: 'CONDITIONAL-MAPPER',
                            config: { type: 'String' },
                            inputs: {
                              booleanSource: {
                                sourceObject: 'newNode2',
                                sourcePath: '$.output',
                              },
                              trueSource: 'Names of floor plans must be unique within a building',
                            },
                            position: {
                              x: 1723.3453384185823,
                              y: 201.22350592650042,
                            },
                          },
                          newNode9: {
                            type: 'ARRAY-FILTER',
                            config: {
                              type: 'Object',
                              functionId: 'filterDuplicateFloorPlanNames::1',
                              inputName: 'in',
                              outputName: 'result',
                              inputData: true,
                            },
                            inputs: {
                              input: {
                                sourceObject: 'FORM',
                                sourcePath: '$.createFloorPlan',
                              },
                              data: {
                                floorPlanName: {
                                  sourceObject: 'FORMVALUE',
                                  sourcePath: '$.createFloorPlan.title',
                                },
                              },
                            },
                            position: {
                              x: 17.884160756501185,
                              y: 742.7888100866824,
                            },
                          },
                          newNode2: {
                            type: 'OR',
                            config: {},
                            inputs: {
                              sources: [
                                {
                                  sourceObject: 'newNode6',
                                  sourcePath: '$.isGreater',
                                },
                                {
                                  sourceObject: 'newNode8',
                                  sourcePath: '$.isGreater',
                                },
                              ],
                            },
                            position: {
                              x: 1605.9976359338061,
                              y: 627.137903861308,
                            },
                          },
                          newNode3: {
                            type: 'ARRAY-LENGTH',
                            config: {},
                            inputs: {
                              input: {
                                sourceObject: 'newNode9',
                                sourcePath: '$.filtered',
                              },
                            },
                            position: {
                              x: 504.3333333333333,
                              y: 660.521670606777,
                            },
                          },
                          newNode8: {
                            type: 'NUMBER-COMPARE',
                            config: {},
                            inputs: {
                              baseNumber: {
                                sourceObject: 'newNode3',
                                sourcePath: '$.length',
                              },
                              compareNumber: 1,
                            },
                            position: {
                              x: 752.3270291568164,
                              y: 714.1741528762806,
                            },
                          },
                        },
                        outputDefinition: {
                          outputNode: 'FIELD_ERROR',
                        },
                      },
                    ],
                    droppableId: 'createFloorPlan',
                  },
                  image: {
                    type: 'imageupload',
                    label: 'Image',
                    min: 1,
                    max: -27,
                    required: true,
                    droppableId: 'createFloorPlan',
                  },
                },
              },
              type: 'group',
              label: 'Create Floor Plan',
              repeats: {
                enabled: true,
                min: 1,
                max: -1,
                addButtonText: 'Add Floor Plan',
                deleteConfirmationMessage: 'Are you sure you want to delete this?',
                allowReordering: true,
                allowInitialDataDelete: true,
                allowAdding: true,
              },
            },
          },
        },
        hasNoAggregateIdCommand: false,
        fabOptionEnabled: true,
        isImportCommand: false,
        isPublic: false,
      },
    ],
  },
};
const FLOOR_PLAN_WEBUI_DEFAULT: WebUIDefault = {
  fabActions: [
    {
      aggregateName: FLOOR_PLAN_AGGREGATE_DEFAULT.typeName,
      button: commandActionButtonActionToRef({
        action: `createFloorPlan ver:1`,
        label: `Add ${
          FLOOR_PLAN_AGGREGATE_DEFAULT.singularName ?? FLOOR_PLAN_AGGREGATE_DEFAULT.typeName
        }`,
        icon: 'fa-object-ungroup',
        conditionalMap: {
          nodes: {
            METADATA: { type: 'METADATA', config: {} },
            BUTTON_STATE: {
              type: 'BUTTON-STATE',
              config: {},
              inputs: {
                isHidden: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
            },
            newNode1: {
              type: 'FIXED-REGEX',
              config: {},
              inputs: {
                text: {
                  sourceObject: 'METADATA',
                  sourcePath: '$.userInfo.permissionString',
                },
                regex: `(:?^|,)(${FLOOR_PLAN_DEFAULT_KEYS.permissionKey})(:?$|,)`,
              },
            },
            newNode2: {
              type: 'NOT',
              config: {},
              inputs: {
                input: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.match',
                },
              },
            },
          },
          outputDefinition: { outputNode: 'BUTTON_STATE' },
        },
      } as ActionButtonRow),
    },
  ],
  aggregateUICustomizations: {
    [FLOOR_PLAN_AGGREGATE_DEFAULT.typeName]: {
      hiddenFromMapView: true,
    },
  },
};

const FLOOR_PLAN_FUNCTIONS = {
  transformFloorPlans: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: {
            result: {
              type: 'object',
              properties: {
                label: { type: 'string' },
                value: { type: 'string' },
              },
              required: ['label', 'value'],
            },
          },
        },
        input: {
          type: 'object',
          properties: { in: { type: 'object', properties: {} } },
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE', name: 'Object Builder' },
              inputs: {
                result: {
                  label: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.title',
                  },
                  value: {
                    sourceObject: 'newNode1',
                    sourcePath: '$.id',
                  },
                },
              },
              name: 'Object Builder',
              position: { x: 1135.5779569892472, y: 192.8481182795699 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: 50, y: 289.1875 },
            },
            newNode1: {
              type: 'OBJECT-ATTRIBUTE-LOOKUP',
              config: {
                props: [
                  ['String', 'title'],
                  ['String', 'id'],
                ],
              },
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in' },
              },
              position: { x: 212.1209677419355, y: 112.77419354838712 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
  filterDuplicateFloorPlanNames: {
    currentVersion: 1,
    versions: [
      {
        versionNumber: 1,
        output: {
          type: 'object',
          properties: { result: { type: 'boolean' } },
          required: ['result'],
        },
        input: {
          type: 'object',
          properties: {
            in: { type: 'object', properties: {} },
            data: {
              type: 'object',
              properties: { floorPlanName: { type: 'string' } },
              required: ['floorPlanName'],
            },
          },
          required: ['data', 'in'],
        },
        aggregateMap: {
          nodes: {
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                result: {
                  sourceObject: 'newNode2',
                  sourcePath: '$.output',
                },
              },
              position: { x: 744, y: 52 },
            },
            DATA: {
              type: 'DATA',
              config: {},
              position: { x: -6, y: 235 },
            },
            newNode1: {
              type: 'OBJECT-ATTRIBUTE-LOOKUP',
              config: { props: [['String', 'title']] },
              inputs: {
                input: { sourceObject: 'DATA', sourcePath: '$.in' },
              },
              position: { x: 5, y: -28 },
            },
            newNode2: {
              type: 'STRING-EQUALS',
              config: {},
              inputs: {
                firstSource: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.title',
                },
                secondSource: {
                  sourceObject: 'DATA',
                  sourcePath: '$.data.floorPlanName',
                },
              },
              position: { x: 491, y: 161 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};

export const generateFloorPlanAggregateData = (config: Gen5Config) => {
  generateAggregateData(config, FLOOR_PLAN_AGGREGATE_DEFAULT, FLOOR_PLAN_WEBUI_DEFAULT);
  addFloorPlanPropertyToAggregates(config);
  addFloorPlanFunctions(config);
};
export const removeFloorPlanAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, FLOOR_PLAN_DEFAULT_KEYS.typeName);
  removePropertiesFromAggregatesByType(config, FLOOR_PLAN_DEFAULT_KEYS.typeName);
  removeTransformFloorPlanFunction(config);
};
export const removeAggregatePropertyFromFloorPlan = (
  config: Gen5Config,
  floorPlanEnabled: boolean,
  typeName: string,
  setAggregateProperties: (aggrIndex: number, aggregateProperties: PropertyCollection) => void
): void => {
  if (floorPlanEnabled) {
    const floorPlan = Object.entries(config.aggregates).filter(
      ([id, aggregate]) => aggregate.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName
    );

    floorPlan.forEach(([index, aggregate]) => {
      const filteredProps: PropertyCollection = {
        id: { type: 'ID', label: 'ID' },
        ...Object.fromEntries(
          Object.entries(aggregate.properties).filter(([, prop]) => prop.type !== typeName)
        ),
      };
      setAggregateProperties(Number(index), filteredProps);
    });
  }
};

const addFloorPlanPropertyToAggregates = (config: Gen5Config) => {
  Object.entries(config.aggregates).forEach(([recordType, aggregate]) => {
    const defaultFloorPlanProperty = {
      nullable: true,
      label: 'Floor Plan',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-ONE',
    };

    if (
      aggregate.typeName !== FLOOR_PLAN_DEFAULT_KEYS.typeName &&
      aggregate.typeName !== BUILDING_DEFAULT_KEYS.typeName &&
      !NOT_MAP_ASSETS_NAMES.includes(aggregate.typeName)
    ) {
      aggregate.properties = {
        ...aggregate.properties,
        floorPlan: {
          ...defaultFloorPlanProperty,
          ...(aggregate.properties?.floorPlan ?? {}),
        },
      };
    }

    if (aggregate.typeName === 'Project') {
      aggregate.properties = {
        ...aggregate.properties,
        floorPlans: {
          nullable: true,
          label: 'Floor Plans',
          type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: 'project',
        },
        buildings: {
          nullable: true,
          label: 'Buildings',
          type: BUILDING_DEFAULT_KEYS.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: 'project',
        },
      };
    }
  });

  const floorPlanAggregate = config.aggregates.find(
    (aggregate) => aggregate.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName
  );

  if (floorPlanAggregate) {
    floorPlanAggregate.properties = {
      ...floorPlanAggregate.properties,
      ...Object.entries(config.aggregates).reduce((acc, [recordType, aggregate]) => {
        if (
          aggregate.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName ||
          aggregate.typeName === BUILDING_DEFAULT_KEYS.typeName ||
          NOT_MAP_ASSETS_NAMES.includes(aggregate.typeName)
        )
          return acc;
        acc[`${camelCase(aggregate.typeName)}s`] = {
          nullable: true,
          label: aggregate.pluralName,
          type: aggregate.typeName,
          relation: 'ONE-TO-MANY',
          foreignColumn: FLOOR_PLAN_DEFAULT_KEYS.foreignKey,
        };
        return acc;
      }, {} as Record<string, any>),
    };
  }
};

const addFloorPlanFunctions = (config: Gen5Config) => {
  if (config?.functions) {
    config.functions.transformFloorPlans =
      FLOOR_PLAN_FUNCTIONS.transformFloorPlans as FunctionDefinition;
    config.functions.filterDuplicateFloorPlanNames =
      FLOOR_PLAN_FUNCTIONS.filterDuplicateFloorPlanNames as FunctionDefinition;
  }
};

const removeTransformFloorPlanFunction = (config: Gen5Config) => {
  if (config?.functions && 'transformFloorPlans' in config.functions) {
    delete config.functions.transformFloorPlans;
  }
  if (config?.functions && 'filterDuplicateFloorPlanNames' in config.functions) {
    delete config.functions.filterDuplicateFloorPlanNames;
  }
};
//#endregion

//#region Building
const BUILDING_AGGREGATE_DEFAULT: AggregateDefault = {
  typeName: BUILDING_DEFAULT_KEYS.typeName,
  singularName: 'Building',
  properties: {
    floorPlans: {
      nullable: true,
      label: 'Floor Plans',
      type: FLOOR_PLAN_DEFAULT_KEYS.typeName,
      relation: 'ONE-TO-MANY',
      foreignColumn: BUILDING_DEFAULT_KEYS.foreignKey,
    },
  },
  isReferenceType: false,
};
BUILDING_AGGREGATE_DEFAULT.events = {
  buildingCreated: {
    versions: [
      {
        versionNumber: 1,
        eventSchema: {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['buildingCreated'] },
            version: { type: 'number' },
            timestamp: {
              description: 'Timestamp',
              type: 'string',
              format: 'date-time',
            },
            aggregateId: { type: 'string' },
            metadata: {
              type: 'object',
              properties: {
                timeFormOpened: {
                  type: 'string',
                  format: 'date-time',
                },
                latitude: { type: 'number' },
                longitude: { type: 'number' },
                selectedLatitude: { type: 'number' },
                selectedLongitude: { type: 'number' },
                locationAccuracy: { type: 'number' },
                aggregateType: { type: 'string' },
                userInfo: {
                  type: 'object',
                  properties: {
                    userName: { type: 'string' },
                    email: { type: 'string' },
                    authExpiration: {
                      type: 'string',
                      format: 'date-time',
                    },
                    familyName: { type: 'string' },
                    givenName: { type: 'string' },
                    authPayload: { type: 'object', properties: {} },
                    roles: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          name: { type: 'string' },
                        },
                      },
                    },
                    roleString: { type: 'string' },
                    permissions: {
                      type: 'array',
                      items: { type: 'string' },
                    },
                    permissionString: { type: 'string' },
                  },
                },
                isOnline: { type: 'boolean' },
                selectedProjects: {
                  type: 'array',
                  items: { type: 'string' },
                },
              },
            },
            data: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                project: { type: 'string' },
                lat: { type: 'number' },
                lon: { type: 'number' },
              },
              required: ['title', 'project', 'lat', 'lon'],
            },
          },
        },
        aggregateMap: {
          nodes: {
            EVENT: {
              type: 'EVENT',
              config: {},
              inputs: {},
              position: { x: 134.3125, y: 50 },
            },
            STATE: {
              type: 'STATE',
              config: {},
              inputs: {},
              position: { x: 138.265625, y: 985.1875 },
            },
            OUTPUT: {
              type: 'OBJECT-BUILDER',
              config: { objectSchema: 'STATE' },
              inputs: {
                title: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.title',
                },
                createdDateTime: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.timestamp',
                },
                createdBy: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.metadata.userInfo.email',
                },
                lat: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lat',
                },
                lon: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.lon',
                },
                project: {
                  sourceObject: 'EVENT',
                  sourcePath: '$.data.project',
                },
                symbolKey: {
                  sourceObject: 'newNode1',
                  sourcePath: '$.output',
                },
              },
              position: {
                x: 587.2182026620326,
                y: 185.99164361356458,
              },
            },
            newNode1: {
              type: 'STRING-CONCAT',
              config: {},
              inputs: { strings: ['symbol_circle_000000__1'] },
              position: { x: 78, y: 360.5 },
            },
          },
          outputDefinition: { outputNode: 'OUTPUT' },
        },
      },
    ],
  },
};

export const generateBuildingAggregateData = (config: Gen5Config) => {
  return generateAggregateData(config, BUILDING_AGGREGATE_DEFAULT);
};
export const removeBuildingAggregateData = (config: Gen5Config) => {
  removeAggregateData(config, BUILDING_DEFAULT_KEYS.typeName);
  removePropertiesFromAggregatesByType(config, BUILDING_DEFAULT_KEYS.typeName);
};
//#endregion

const generateAggregateData = (
  config: Gen5Config,
  aggregateDefault: AggregateDefault,
  webUIDefault?: WebUIDefault
) => {
  const aggregateIndex = getAggregateIndex(config, aggregateDefault.typeName);

  const aggregate =
    (aggregateIndex !== -1 && config.aggregates[aggregateIndex]) ||
    generateAggregateByName(aggregateDefault.typeName);
  aggregate.singularName = aggregateDefault.singularName ?? aggregateDefault.typeName;
  aggregate.pluralName = `${aggregateDefault.singularName ?? aggregateDefault.typeName}s`;
  aggregate.isReferenceType = aggregateDefault.isReferenceType;

  if (aggregateDefault.properties) {
    // Assign default values to standard aggregate properties (this preserves property order and values not specified in the default).
    const standardKeys = Object.keys(aggregate.properties);
    standardKeys.forEach((propertyName) => {
      const standardDefault = aggregateDefault.properties![propertyName];
      if (standardDefault) _.assign(aggregate.properties[propertyName], standardDefault);
    });
    // Push non-standard aggregate properties.
    const nonStandardProps = standardKeys.reduce((acc, cur, i, a) => {
      const { [cur]: _, ...rest } = acc;
      return rest;
    }, aggregateDefault.properties);

    const filteredProps: PropertyCollection = {
      id: { type: 'ID', label: 'ID' },
      ...Object.fromEntries(
        Object.entries(aggregate.properties).filter(([key]) => key !== 'lat' && key !== 'lon')
      ),
    };

    aggregate.properties = {
      ...(aggregateDefault.typeName === FLOOR_PLAN_DEFAULT_KEYS.typeName
        ? filteredProps
        : aggregate.properties),
      ...(nonStandardProps as PropertyCollection),
    };
  }

  generateAggregateEvents(aggregate, aggregateDefault.events);
  generateAggregateCommands(aggregate, aggregateDefault.commands);

  if (aggregateIndex !== -1) config.aggregates[aggregateIndex] = aggregate;
  else config.aggregates.push(aggregate);

  if (webUIDefault) {
    generateWebUI(config.webUIConfig, webUIDefault, aggregate);
  }

  return config;
};

const generateAggregateEvents = (
  aggregate: AggregateConfig,
  eventDefaults: AggregateDefault['events']
) => {
  aggregate.events = aggregate.events ?? {};

  Object.entries(eventDefaults ?? {}).forEach(([eventName, eventDefinition]) => {
    const versionDefaults = eventDefinition?.versions as EventVersionDefinition[];
    const newEvent = generateNewEventAndVersions(
      eventName,
      versionDefaults.map<EventVersionDefault>((v) => ({
        versionNumber: v.versionNumber,
        dataSchema: v.eventSchema?.properties?.data as JSONSchema6,
        aggregateMap: v.aggregateMap,
      }))
    );

    aggregate.events = { ...aggregate.events, [eventName]: newEvent };
  });

  return aggregate;
};

const generateAggregateCommands = (
  aggregate: AggregateConfig,
  commandDefaults: AggregateDefault['commands']
) => {
  aggregate.commands = aggregate.commands ?? {};

  Object.entries(commandDefaults ?? {}).forEach(([commandName, commandDefinition]) => {
    const versionDefaults = commandDefinition?.versions as CommandVersionDefinition[];
    const newCommand = generateNewCommandAndVersions(
      commandName,
      versionDefaults.map<CommandVersionDefault>((v) => ({
        commandType: v.commandType,
        version: v.version,
        eventName:
          v.cmdToEventsMapping?.mappingType === 'ONE-TO-MANY'
            ? undefined
            : v.cmdToEventsMapping?.eventName,
        isCreationCommand: v.isCreationCommand,
        cmdToEventsMapping: v.cmdToEventsMapping,
        formDefinition: v.commandType === 'FORM' ? v.formDefinition : undefined,
        hasNoAggregateIdCommand: v.hasNoAggregateIdCommand,
        fabOptionEnabled: v.fabOptionEnabled,
      })),
      commandDefinition?.defaultLabel
    );

    aggregate.commands = { ...aggregate.commands, [commandName]: newCommand };
  });

  return aggregate;
};

const generateWebUI = (
  webUI: WebUIConfig,
  webUIDefault: WebUIDefault,
  aggregate?: AggregateConfig | AggregateDefault
) => {
  // FAB Actions
  if (webUIDefault.fabActions) {
    const faDefaults = webUIDefault.fabActions as FabAction[];
    faDefaults
      .filter((fa) => !aggregate?.typeName || fa.aggregateName === aggregate.typeName)
      .forEach((fa) => webUI.fabActions.push(fa));
  }

  // Aggregate Customizations
  if (webUIDefault.aggregateUICustomizations) {
    webUI.aggregateUICustomizations = webUI.aggregateUICustomizations ?? {};

    const acDefaults =
      webUIDefault.aggregateUICustomizations as WebUIConfig['aggregateUICustomizations'];
    Object.entries(acDefaults ?? {})
      .filter(([acName]) => !aggregate?.typeName || acName === aggregate.typeName)
      .forEach(([acName, acDefinition]) => {
        webUI.aggregateUICustomizations![acName] = {
          ...(webUI.aggregateUICustomizations![acName] ??
            defaultAggregateUICustomization(
              'webUIConfig',
              aggregate?.labelProperty ?? AGGREGATE_DEFAULT_KEYS.labelProperty
            )),
          ...acDefinition,
        };
      });
  }

  return webUI;
};

const removeAggregateData = (config: Gen5Config, aggregateTypeName: string) => {
  // Aggregate, Events, Commands
  const aggregateIndex = getAggregateIndex(config, aggregateTypeName);
  if (aggregateIndex !== -1) {
    config.aggregates.splice(aggregateIndex, 1);
  }

  // WebUI
  if (
    config.webUIConfig.fabActions &&
    config.webUIConfig.fabActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !== -1
  ) {
    config.webUIConfig.fabActions = [
      ...config.webUIConfig.fabActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.webUIConfig.importActions &&
    config.webUIConfig.importActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !==
      -1
  ) {
    config.webUIConfig.importActions = [
      ...config.webUIConfig.importActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.webUIConfig.aggregateUICustomizations &&
    config.webUIConfig.aggregateUICustomizations[aggregateTypeName]
  ) {
    delete config.webUIConfig.aggregateUICustomizations[aggregateTypeName];
  }

  // MobileUI
  if (
    config.mobileUIConfig.fabActions &&
    config.mobileUIConfig.fabActions.findIndex((fa) => fa.aggregateName === aggregateTypeName) !==
      -1
  ) {
    config.mobileUIConfig.fabActions = [
      ...config.mobileUIConfig.fabActions.filter((fa) => fa.aggregateName !== aggregateTypeName),
    ];
  }
  if (
    config.mobileUIConfig.aggregateUICustomizations &&
    config.mobileUIConfig.aggregateUICustomizations[aggregateTypeName]
  ) {
    delete config.mobileUIConfig.aggregateUICustomizations[aggregateTypeName];
  }

  // Schedules and Custom Pages
  if (config.schedules && config.schedules[aggregateTypeName]) {
    delete config.schedules[aggregateTypeName];
  }
  if (config.pagesConfig) {
    Object.entries(config.pagesConfig).forEach(([pageName, pageConfig]) => {
      if (pageConfig.aggregateType === aggregateTypeName) {
        delete config.pagesConfig![pageName];
      }
    });
  }

  return config;
};

const removePropertiesFromAggregatesByType = (config: Gen5Config, aggregateTypeName: string) => {
  Object.entries(config.aggregates).forEach(([recordType, aggregate]) => {
    const filteredProps: PropertyCollection = {
      id: { type: 'ID', label: 'ID' },
      ...Object.fromEntries(
        Object.entries(aggregate.properties).filter(([, prop]) => prop.type !== aggregateTypeName)
      ),
    };

    aggregate.properties = filteredProps;
  });
};
